import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FIRST_AUTH_LOGIN, SSO_PROVIDER_LIST_GET } from '../../redux/actions';
import {
  AuthContainer,
  FormContainer,
  InputWrapper,
  LogoContainer,
  ResetPassword,
  CheckboxWrapper,
  UsernameWrapper,
  PasswordWrapper,
  ButtonsWrapper,
  DisabledMessage,
} from './styles';
import {
  CTA,
  Checkbox,
  CustomInput,
  CustomSelect,
  Logo,
  Text,
} from '../../../ui/atoms';
import {
  getValues,
  isValidForm,
  newForm,
  newFormField,
} from '../../../utils/form';
import { RegexpType, validator } from '../../../utils/validator';
import routes from '../../../routes';
import { MODULES } from '../../../utils/modules';
import { apiHost, feBaseUrl } from '../../api/config';
import { Container } from '../../../ui/gridSystem';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import CustomTooltip from '../../../ui/atoms/CustomTooltip';
import InputLegend from '../../../ui/atoms/InputLegend';
import {
  getPageDescriptionByPath,
  getPageTitleByPath,
} from '../../../utils/labels';

const initialAuthForm = newForm([
  newFormField({
    field: 'username',
    required: true,
    type: RegexpType.STRING,
    valid: true,
  }),
  newFormField({
    field: 'password',
    required: true,
    type: RegexpType.PASSWORD,
    valid: true,
  }),
]);

const Auth = ({
  mediaIsPhone,
  startLogin,
  modules,
  getProviderSSO,
  providerList,
  appConfig,
}) => {
  sessionStorage.removeItem('isLogout');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [authForm, setAuthForm] = useState(initialAuthForm);
  const [ssoUrl, setSsoUrl] = useState();
  const [loginType, setloginType] = useState();
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [contacts, setContacts] = useState();
  const [pwdShow, setPwdShow] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getProviderSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = (field = '', newValue) => {
    const { type, required } = authForm[field];
    setAuthForm({
      ...authForm,
      [field]: {
        ...authForm[field],
        value: newValue,
        valid:
          typeof newValue !== 'object'
            ? validator(type, newValue, required)
            : true,
      },
    });
  };

  const onKeyPressPwd = (event) => {
    if (event.key === 'Enter' && privacyChecked) {
      event.preventDefault();
      startLogin(getValues(authForm));
    }
  };
  const onClickLogo = modules.includes(MODULES.PUBLIC_SITE)
    ? () => navigate(routes.public.path)
    : undefined;

  const togglePrivacy = () => setPrivacyChecked(!privacyChecked);

  return (
    <>
      <Helmet>
        <title>{getPageTitleByPath(pathname)}</title>
        <link rel="canonical" href={`${feBaseUrl}${pathname}`} />
        {getPageDescriptionByPath(pathname) && (
          <meta
            name="description"
            content={getPageDescriptionByPath(pathname)}
          />
        )}
      </Helmet>
      <AuthContainer>
        <FormContainer>
          <LogoContainer>
            <Logo
              src={appConfig?.login_logo?.src}
              alt={appConfig?.login_logo?.alt}
              size={Logo.SIZE.XL}
              onClick={onClickLogo}
            />
          </LogoContainer>
          <Container className="pt-0">
            <Text type="h1" mb={8} className="title">
              Accedi all'area personale
            </Text>
            <Text type="b2" mb={24}>
              Per accedere selezionare la propria categoria di appartenenza
            </Text>
            <InputLegend
              label="Categoria di appartenenza"
              htmlFor="ffoo-choice"
            />
            <CustomSelect
              options={[...(providerList || [])].sort(
                (a, b) => a.weight - b.weight
              )}
              onChange={(v) => {
                setloginType(v?.type);
                if (v?.email || v?.numero_telefono) {
                  setContacts({
                    email: v?.email,
                    phone: v?.numero_telefono,
                  });
                } else setContacts();
                const url =
                  v?.sso_forced_relay_state_url ||
                  `${apiHost}/saml_login_fe?provider-key=${v?.provider_key}`;
                setSsoUrl(url);
              }}
              id="ffoo-choice"
            />
            {/* credenziali  */}
            {(loginType === 'credenziali' ||
              loginType === 'sso_credenziali') && (
              <>
                <InputWrapper>
                  <InputLegend
                    label="Username"
                    htmlFor="login-username"
                    tooltipKey={mediaIsPhone ? 'login_username' : null}
                  />
                  <UsernameWrapper>
                    <CustomInput
                      placeholder="Username"
                      onChange={(newValue) =>
                        handleOnChange('username', newValue)
                      }
                      value={authForm?.username?.value}
                      valid={authForm?.username?.valid}
                      id="login-username"
                    />
                    {!mediaIsPhone && (
                      <CustomTooltip
                        title="Username"
                        tooltipKey="login_username"
                      />
                    )}
                  </UsernameWrapper>
                </InputWrapper>
                <InputWrapper>
                  <InputLegend label="Password" htmlFor="login-password" />
                  <PasswordWrapper>
                    <CustomInput
                      placeholder="Password"
                      id="login-password"
                      onChange={(newValue) =>
                        handleOnChange('password', newValue)
                      }
                      onKeyPress={onKeyPressPwd}
                      type={pwdShow ? 'text' : 'password'}
                      value={authForm?.password?.value}
                      valid={authForm?.password?.valid}
                      error="La password deve contenere almeno 4 caratteri"
                    />
                    <CTA
                      type="tertiary"
                      btnText={
                        pwdShow ? 'Nascondi password' : 'Mostra password'
                      }
                      onClick={() => setPwdShow(!pwdShow)}
                      icon={pwdShow ? 'eye-off' : 'eye'}
                      hiddenLabel
                    />
                  </PasswordWrapper>
                </InputWrapper>
                <ResetPassword onClick={() => navigate(routes.resetAuth.path)}>
                  <Text type="b3">Hai dimenticato la password?</Text>
                </ResetPassword>
              </>
            )}
            {/* Contacts */}
            {contacts && (
              <>
                <Text type="h3" mt={12} mb={6}>
                  Contatti Help-Desk SSO
                </Text>
                {contacts.email && (
                  <Text type="b3" mb={0} className="contacts d-flex">
                    <Text type="h3.5">Email: </Text>
                    <a href={`mailto:${contacts.email}`}>{contacts.email}</a>
                  </Text>
                )}
                {contacts.phone && (
                  <Text type="b3" mb={8} className="contacts d-flex">
                    <Text type="h3.5">Telefono: </Text>
                    <a className="link" href={`tel:${contacts.phone}`}>
                      {contacts.phone}
                    </a>
                  </Text>
                )}
              </>
            )}
            {loginType === 'disabilitato' && (
              <DisabledMessage>
                Attualmente l'accesso alla piattaforma è disabilitato.{' '}
              </DisabledMessage>
            )}
            {/* Confirm btns */}
            {loginType && loginType !== 'disabilitato' && (
              <>
                <CheckboxWrapper>
                  <Checkbox
                    label={appConfig.privacy_message}
                    onChange={togglePrivacy}
                    required
                  />
                </CheckboxWrapper>
                <div style={{ paddingLeft: 20, paddingTop: 6 }}>
                  <span className="mandatory" />
                  Campo obbligatorio
                </div>
                <InputWrapper>
                  {loginType === 'credenziali' ? (
                    <CTA
                      btnText="Accedi"
                      disabled={!isValidForm(authForm) || !privacyChecked}
                      onClick={() => startLogin(getValues(authForm), navigate)}
                      fullsize={mediaIsPhone}
                    />
                  ) : loginType === 'sso_credenziali' ? (
                    <ButtonsWrapper>
                      <CTA
                        btnText="Accedi"
                        disabled={!isValidForm(authForm) || !privacyChecked}
                        onClick={() =>
                          startLogin(getValues(authForm), navigate)
                        }
                        fullsize={mediaIsPhone}
                      />
                      <CTA
                        btnText="ACCEDI TRAMITE SSO"
                        onClick={() => window.open(ssoUrl, '_self')}
                        height={48}
                        classes="login-sso"
                        disabled={!privacyChecked}
                        fullsize={mediaIsPhone}
                      />
                    </ButtonsWrapper>
                  ) : (
                    <CTA
                      btnText="ACCEDI TRAMITE SSO"
                      onClick={() => window.open(ssoUrl, '_self')}
                      height={48}
                      classes="login-sso"
                      disabled={!privacyChecked}
                      fullsize={mediaIsPhone}
                    />
                  )}
                </InputWrapper>
              </>
            )}
          </Container>
        </FormContainer>
      </AuthContainer>
    </>
  );
};

Auth.propTypes = {
  mediaIsPhone: PropTypes.bool,
  modules: PropTypes.array.isRequired,
  providerList: PropTypes.array,
  appConfig: PropTypes.object,
  // HOC (connect, dispatch)
  startLogin: PropTypes.func.isRequired,
  getProviderSSO: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { providerList, appConfig } = state.app;
    const { modules } = state.modules;
    return {
      modules,
      providerList,
      appConfig,
    };
  },
  (dispatch) => ({
    startLogin: (params, navigate) =>
      dispatch({ type: FIRST_AUTH_LOGIN._REQUEST, params, navigate }),
    getProviderSSO: () => dispatch({ type: SSO_PROVIDER_LIST_GET._REQUEST }),
  })
)(withMediaQueries(Auth));
